import React, { useContext, useEffect, useState } from "react";
import Visual from "../Examination/PhysioScales/MOCAScaleComponents/Visual";
import Memory from "../Examination/PhysioScales/MOCAScaleComponents/Memory";
import Language from "../Examination/PhysioScales/MOCAScaleComponents/Language";
import DelayedRecall from "../Examination/PhysioScales/MOCAScaleComponents/DelayedRecall";
import Orientation from "../Examination/PhysioScales/MOCAScaleComponents/Orientation";
import Abstraction from "../Examination/PhysioScales/MOCAScaleComponents/Abstraction";
import Naming from "../Examination/PhysioScales/MOCAScaleComponents/Naming";
import Attention from "../Examination/PhysioScales/MOCAScaleComponents/Attention";
import { Col, Collapse, Form, Input, List, Row, Typography } from "antd";
import { checkEmpty } from "../../../../helpers/utility";
import "../Examination/PhysioScales/PhysioScales.css";
const { Panel } = Collapse;

const getMocaData = (formData) =>
  formData?.scales?.moca || formData?.systemic_examination?.moca || {};

const PsychologistMocaScale = ({ form, context }) => {
  const { formData } = useContext(context);
  const mocaData = getMocaData(formData);

  const [visual, setVisual] = useState(mocaData?.visual_executive_data || {});
  const [naming, setNaming] = useState(mocaData?.naming_data || {});
  const [attention, setAttention] = useState(mocaData?.attention_data || {});
  const [language, setLanguage] = useState(mocaData?.language_data || {});
  const [abstraction, setAbstraction] = useState(
    mocaData?.abstraction_data || {}
  );
  const [delayedRecall, setDelayedRecall] = useState(
    mocaData?.delayed_recall_data || {}
  );
  const [orientation, setOrientation] = useState(
    mocaData?.orientation_data || {}
  );

  useEffect(() => {
    const next = getMocaData(formData);
    setVisual(next?.visual_executive_data || {});
    setNaming(next?.naming_data || {});
    setAttention(next?.attention_data || {});
    setLanguage(next?.language_data || {});
    setAbstraction(next?.abstraction_data || {});
    setDelayedRecall(next?.delayed_recall_data || {});
    setOrientation(next?.orientation_data || {});
  }, [formData]);

  const calculateAggregate = (states, special, key) => {
    let sum = 0;
    if (special === false) {
      sum = Object.values(states).reduce(
        (acc, cur) => acc + (cur === "yes" ? 1 : 0),
        0
      );
    } else {
      sum = Object.values(states).reduce(
        (acc, cur) => acc + (cur === "yes" ? 1 : 0),
        0
      );
      const len = states?.attention_4?.length || 0;
      sum += len >= 4 ? 3 : len >= 2 ? 2 : len;
    }
    form.setFieldValue(["moca", key], sum);
    return sum;
  };

  const visualAggregate = checkEmpty(visual)
    ? calculateAggregate(visual, false, "visual_executive")
    : mocaData?.visual_executive ?? 0;
  const namingAggregate = checkEmpty(naming)
    ? calculateAggregate(naming, false, "naming")
    : mocaData?.naming ?? 0;
  const attentionAggregate = checkEmpty(attention)
    ? calculateAggregate(attention, true, "attention")
    : mocaData?.attention ?? 0;
  const languageAggregate = checkEmpty(language)
    ? calculateAggregate(language, false, "language")
    : mocaData?.language ?? 0;
  const abstractionAggregate = checkEmpty(abstraction)
    ? calculateAggregate(abstraction, false, "abstraction")
    : mocaData?.abstraction ?? 0;
  const delayedRecallAggregate = checkEmpty(delayedRecall)
    ? calculateAggregate(delayedRecall, false, "delayed_recall")
    : mocaData?.delayed_recall ?? 0;
  const orientationAggregate = checkEmpty(orientation)
    ? calculateAggregate(orientation, false, "orientation")
    : mocaData?.orientation ?? 0;

  const hasSectionData =
    checkEmpty(visual) ||
    checkEmpty(naming) ||
    checkEmpty(attention) ||
    checkEmpty(language) ||
    checkEmpty(abstraction) ||
    checkEmpty(delayedRecall) ||
    checkEmpty(orientation);

  const computedAggregate =
    (visualAggregate || 0) +
    (namingAggregate || 0) +
    (attentionAggregate || 0) +
    (languageAggregate || 0) +
    (abstractionAggregate || 0) +
    (delayedRecallAggregate || 0) +
    (orientationAggregate || 0);

  const aggregate = hasSectionData
    ? computedAggregate
    : mocaData?.score ?? computedAggregate;

  const interpretation =
    aggregate >= 26 ? "Normal Cognition" : "Abnormal Cognition";

  useEffect(() => {
    form.setFieldsValue({
      moca: {
        score: aggregate,
        interpretation,
      },
    });
  }, [aggregate, interpretation, form]);

  const renderPanelHeader = (title, score, total) => (
    <Row className="moca-panel-header" gutter={[8, 4]}>
      <Col xs={24} sm={12} className="moca-panel-header__title">
        {title}
      </Col>
      <Col xs={24} sm={12} className="moca-panel-header__score">
        Score: {score} / {total}
      </Col>
    </Row>
  );

  return (
    <>
      <div className="physio-scales psychologist-moca-scale">
        <List bordered>
          <List.Item>
            <Typography.Text strong>
              MOCA Score: {aggregate}/30
            </Typography.Text>
            <Form.Item name={["moca", "score"]} hidden>
              <Input />
            </Form.Item>
          </List.Item>
          <List.Item>
            <Typography.Text strong>
              Interpretation: {interpretation}
            </Typography.Text>
            <Form.Item name={["moca", "interpretation"]} hidden>
              <Input />
            </Form.Item>
          </List.Item>
        </List>
        <Form.Item name={["moca", "visual_executive"]} hidden />
        <Form.Item name={["moca", "naming"]} hidden />
        <Form.Item name={["moca", "attention"]} hidden />
        <Form.Item name={["moca", "language"]} hidden />
        <Form.Item name={["moca", "abstraction"]} hidden />
        <Form.Item name={["moca", "delayed_recall"]} hidden />
        <Form.Item name={["moca", "orientation"]} hidden />

        <Collapse
          className="moca-collapse"
          defaultActiveKey={["1", "2", "3", "4", "5", "6", "7", "8"]}
        >
          <Panel
            header={renderPanelHeader("Visual/Executive", visualAggregate, 5)}
            key="1"
          >
            <Visual psych={true} setVisual={setVisual} />
          </Panel>
          <Panel
            header={renderPanelHeader("Naming", namingAggregate, 3)}
            key="2"
          >
            <Naming psych={true} setNaming={setNaming} />
          </Panel>
          <Panel header="Memory" key="3">
            <Memory psych={true} />
          </Panel>
          <Panel
            header={renderPanelHeader("Attention", attentionAggregate, 6)}
            key="4"
          >
            <Attention psych={true} setAttention={setAttention} />
          </Panel>
          <Panel
            header={renderPanelHeader("Language", languageAggregate, 3)}
            key="5"
          >
            <Language psych={true} setLanguage={setLanguage} />
          </Panel>
          <Panel
            header={renderPanelHeader("Abstraction", abstractionAggregate, 2)}
            key="6"
          >
            <Abstraction psych={true} setAbstraction={setAbstraction} />
          </Panel>
          <Panel
            header={renderPanelHeader(
              "Delayed Recall",
              delayedRecallAggregate,
              5
            )}
            key="7"
          >
            <DelayedRecall psych={true} setDelayedRecall={setDelayedRecall} />
          </Panel>
          <Panel
            header={renderPanelHeader("Orientation", orientationAggregate, 6)}
            key="8"
          >
            <Orientation psych={true} setOrientation={setOrientation} />
          </Panel>
        </Collapse>
      </div>
    </>
  );
};

export default PsychologistMocaScale;
